import { Z_INDEX } from '../shared/constants/theme';

/**
 * Overlay props for MUI Select / Menu / Autocomplete / DatePicker inside Drawers.
 *
 * Mobile shell raises Drawer z-index to 1700; default Menu/Modal portals sit at ~1300,
 * so dropdowns open behind the drawer on web mobile. These helpers portal overlays
 * above the drawer (DRAWER_MENU / DRAWER_POPOVER = 1800).
 */

function paperSx(isDark, surfaceBg) {
  return {
    maxHeight: 'min(320px, 50vh)',
    bgcolor: surfaceBg ?? (isDark ? '#1a1e28' : '#ffffff'),
    backgroundImage: 'none',
    color: isDark ? '#ddd' : '#333',
    boxShadow: isDark
      ? '0 12px 32px rgba(0,0,0,0.45)'
      : '0 8px 24px rgba(0,0,0,0.12)',
  };
}

/** MUI Select `MenuProps` / Menu paper props for use inside drawers */
export function getDrawerSelectMenuProps(isDark = true, surfaceBg) {
  return {
    disablePortal: false,
    sx: { zIndex: Z_INDEX.DRAWER_MENU },
    PaperProps: {
      sx: {
        zIndex: Z_INDEX.DRAWER_MENU,
        ...paperSx(isDark, surfaceBg),
      },
    },
    MenuListProps: { dense: true },
  };
}

/** Alias — standalone Menu uses the same shape */
export function getDrawerMenuProps(isDark = true, surfaceBg) {
  return getDrawerSelectMenuProps(isDark, surfaceBg);
}

/** Autocomplete `slotProps` for drawers */
export function getDrawerAutocompleteSlotProps(isDark = true, surfaceBg) {
  return {
    popper: {
      disablePortal: false,
      sx: { zIndex: Z_INDEX.DRAWER_MENU },
    },
    paper: {
      sx: {
        zIndex: Z_INDEX.DRAWER_MENU,
        ...paperSx(isDark, surfaceBg),
      },
    },
    listbox: {
      sx: { maxHeight: 'min(320px, 50vh)' },
    },
  };
}

/** DatePicker / TimePicker / DateTimePicker `slotProps` for drawers */
export function getDrawerDatePickerSlotProps(isDark = true, surfaceBg) {
  const bg = surfaceBg ?? (isDark ? '#1a1e28' : '#ffffff');
  return {
    popper: {
      disablePortal: false,
      sx: { zIndex: Z_INDEX.DRAWER_POPOVER },
    },
    desktopPaper: {
      sx: {
        zIndex: Z_INDEX.DRAWER_POPOVER,
        bgcolor: bg,
        backgroundImage: 'none',
        color: isDark ? '#ddd' : '#333',
      },
    },
    mobilePaper: {
      sx: { zIndex: Z_INDEX.DRAWER_POPOVER },
    },
  };
}

/** Drawer root `sx` — keep shell drawers above page chrome */
export function getDrawerRootSx(extra = {}) {
  return {
    zIndex: Z_INDEX.DRAWER,
    ...extra,
  };
}

/** Bundle for forms that need all overlay types */
export function createDrawerOverlayProps(isDark = true, surfaceBg) {
  const menuProps = getDrawerSelectMenuProps(isDark, surfaceBg);
  return {
    selectMenuProps: menuProps,
    menuProps,
    autocompleteSlotProps: getDrawerAutocompleteSlotProps(isDark, surfaceBg),
    datePickerSlotProps: getDrawerDatePickerSlotProps(isDark, surfaceBg),
    drawerRootSx: getDrawerRootSx(),
  };
}
